
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useLanguage } from "@/context/LanguageContext";
import {
  Slider
} from "@/components/ui/slider";

export interface FilterOptions {
  utilities: string[];
  bedrooms: number[];
  bathrooms: number[];
  petAllowed?: boolean;
  immigrantFriendly?: boolean;
  easyApply?: boolean;
  flexibleContract?: boolean;
  minRent?: number;
  maxRent?: number;
  minRating?: number;
  housingTypes?: string[];
}

interface FilterPanelProps {
  isOpen?: boolean;
  onClose?: () => void;
  filters: FilterOptions;
  setFilters: (filters: FilterOptions) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  activeFilterCount: number;
}

export const FilterPanel = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  applyFilters,
  resetFilters,
  activeFilterCount
}: FilterPanelProps) => {
  const { t } = useLanguage();
  const [localFilters, setLocalFilters] = useState<FilterOptions>({...filters});
  
  const handleApply = () => {
    setFilters(localFilters);
    applyFilters();
    if (onClose) onClose();
  };
  
  const handleReset = () => {
    resetFilters();
    setLocalFilters({
      utilities: [],
      bedrooms: [],
      bathrooms: [],
      housingTypes: []
    });
  };
  
  const handleUtilityChange = (checked: boolean | "indeterminate", utility: string) => {
    if (checked === "indeterminate") return;
    
    setLocalFilters(prev => {
      if (checked) {
        return { ...prev, utilities: [...prev.utilities, utility] };
      } else {
        return { ...prev, utilities: prev.utilities.filter(u => u !== utility) };
      }
    });
  };
  
  const handleBedroomChange = (checked: boolean | "indeterminate", value: number) => {
    if (checked === "indeterminate") return;
    
    setLocalFilters(prev => {
      if (checked) {
        return { ...prev, bedrooms: [...prev.bedrooms, value] };
      } else {
        return { ...prev, bedrooms: prev.bedrooms.filter(b => b !== value) };
      }
    });
  };
  
  const handleBathroomChange = (checked: boolean | "indeterminate", value: number) => {
    if (checked === "indeterminate") return;
    
    setLocalFilters(prev => {
      if (checked) {
        return { ...prev, bathrooms: [...prev.bathrooms, value] };
      } else {
        return { ...prev, bathrooms: prev.bathrooms.filter(b => b !== value) };
      }
    });
  };
  
  const handleHousingTypeChange = (checked: boolean | "indeterminate", type: string) => {
    if (checked === "indeterminate") return;
    
    setLocalFilters(prev => {
      const housingTypes = prev.housingTypes || [];
      if (checked) {
        return { ...prev, housingTypes: [...housingTypes, type] };
      } else {
        return { ...prev, housingTypes: housingTypes.filter(t => t !== type) };
      }
    });
  };

  const handleRatingChange = (checked: boolean | "indeterminate", rating: number) => {
    if (checked === "indeterminate") return;
    
    setLocalFilters(prev => {
      if (checked) {
        return { ...prev, minRating: rating };
      } else {
        return { ...prev, minRating: undefined };
      }
    });
  };

  const handleRentRangeChange = (values: number[]) => {
    setLocalFilters(prev => ({ 
      ...prev, 
      minRent: values[0], 
      maxRent: values[1] 
    }));
  };
  
  return (
    <div className="py-6 space-y-6 max-h-[80vh] overflow-y-auto">
      {/* Property Features */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('propertyFeatures')}</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="pet-allowed"
              checked={localFilters.petAllowed || false}
              onCheckedChange={(checked) => 
                setLocalFilters(prev => ({ ...prev, petAllowed: checked === true ? true : false }))
              }
            />
            <label htmlFor="pet-allowed" className="text-sm">
              {t('petAllowed')}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="immigrant-friendly"
              checked={localFilters.immigrantFriendly || false} 
              onCheckedChange={(checked) => 
                setLocalFilters(prev => ({ ...prev, immigrantFriendly: checked === true ? true : false }))
              }
            />
            <label htmlFor="immigrant-friendly" className="text-sm">
              {t('immigrantFriendly')}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="easy-apply"
              checked={localFilters.easyApply || false} 
              onCheckedChange={(checked) => 
                setLocalFilters(prev => ({ ...prev, easyApply: checked === true ? true : false }))
              }
            />
            <label htmlFor="easy-apply" className="text-sm">
              {t('easyApply')}
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="flexible-contract"
              checked={localFilters.flexibleContract || false} 
              onCheckedChange={(checked) => 
                setLocalFilters(prev => ({ ...prev, flexibleContract: checked === true ? true : false }))
              }
            />
            <label htmlFor="flexible-contract" className="text-sm">
              {t('flexibleContract')}
            </label>
          </div>
        </div>
      </div>
      
      {/* Housing Types */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('housingTypes')}</h4>
        <div className="grid grid-cols-2 gap-2">
          {['house', 'apartment', 'condo', 'basement', 'room', 'semi-detached'].map((type) => (
            <div key={`housing-${type}`} className="flex items-center space-x-2">
              <Checkbox 
                id={`housing-${type}`}
                checked={(localFilters.housingTypes || []).includes(type)}
                onCheckedChange={(checked) => handleHousingTypeChange(checked, type)}
              />
              <label htmlFor={`housing-${type}`} className="text-sm">
                {t(`form.housingTypes.${type}`)}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Utilities */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('includedUtilities')}</h4>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="water"
              checked={localFilters.utilities.includes('water')}
              onCheckedChange={(checked) => handleUtilityChange(checked, 'water')}
            />
            <label htmlFor="water" className="text-sm">{t('utilities.water')}</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="electricity"
              checked={localFilters.utilities.includes('electricity')}
              onCheckedChange={(checked) => handleUtilityChange(checked, 'electricity')}
            />
            <label htmlFor="electricity" className="text-sm">{t('utilities.electricity')}</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="heating"
              checked={localFilters.utilities.includes('heating')}
              onCheckedChange={(checked) => handleUtilityChange(checked, 'heating')}
            />
            <label htmlFor="heating" className="text-sm">{t('utilities.heating')}</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="internet"
              checked={localFilters.utilities.includes('internet')}
              onCheckedChange={(checked) => handleUtilityChange(checked, 'internet')}
            />
            <label htmlFor="internet" className="text-sm">{t('utilities.internet')}</label>
          </div>
        </div>
      </div>
      
      {/* Bedrooms */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('bedrooms')}</h4>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3].map((num) => (
            <div key={`bedroom-${num}`} className="flex items-center space-x-2">
              <Checkbox 
                id={`bedroom-${num}`}
                checked={localFilters.bedrooms.includes(num)}
                onCheckedChange={(checked) => handleBedroomChange(checked, num)}
              />
              <label htmlFor={`bedroom-${num}`} className="text-sm">
                {num === 3 ? `${num}+` : num}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Bathrooms */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('bathrooms')}</h4>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3].map((num) => (
            <div key={`bathroom-${num}`} className="flex items-center space-x-2">
              <Checkbox 
                id={`bathroom-${num}`}
                checked={localFilters.bathrooms.includes(num)}
                onCheckedChange={(checked) => handleBathroomChange(checked, num)}
              />
              <label htmlFor={`bathroom-${num}`} className="text-sm">
                {num === 3 ? `${num}+` : num}
              </label>
            </div>
          ))}
        </div>
      </div>
      
      {/* Minimum Rating */}
      <div className="space-y-4">
        <h4 className="font-medium">{t('minRating')}</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="rating-3"
              checked={localFilters.minRating === 3}
              onCheckedChange={(checked) => handleRatingChange(checked, 3)}
            />
            <label htmlFor="rating-3" className="text-sm">3+ {t('stars')}</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="rating-4"
              checked={localFilters.minRating === 4}
              onCheckedChange={(checked) => handleRatingChange(checked, 4)}
            />
            <label htmlFor="rating-4" className="text-sm">4+ {t('stars')}</label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="rating-5"
              checked={localFilters.minRating === 5}
              onCheckedChange={(checked) => handleRatingChange(checked, 5)}
            />
            <label htmlFor="rating-5" className="text-sm">5 {t('stars')}</label>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-between items-center">
        {activeFilterCount > 0 ? (
          <div className="text-sm text-muted-foreground">
            {t('activeFilters', { count: activeFilterCount })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {t('noActiveFilters')}
          </div>
        )}
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleReset}
            size="sm"
          >
            {t('clearFilters')}
          </Button>
          
          <Button 
            onClick={handleApply}
            size="sm"
          >
            {t('applyFilters')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
